import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Calendar,
  User,
  ArrowLeft,
  Tag,
  Loader2,
  BookOpen,
  Share2,
  Link2,
  Check,
  FileQuestion,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { mockBlogPosts } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SectionHeader from '../components/SectionHeader';
import IconBadge from '../components/IconBadge';
import Reveal from '../components/Reveal';
import { usePageMeta } from '../components/Seo';
import { useBrandName } from '../context/SettingsContext';
import { BACKEND_URL, DEFAULT_BRAND, BTN_OUTLINE, BTN_PRIMARY } from '../config/brand';

const mapApiPost = (p) => ({
  id: p.id,
  slug: p.slug || p.id,
  title: p.title,
  excerpt: p.excerpt || p.summary || '',
  content: p.content || p.excerpt || '',
  author: p.author || 'Équipe',
  date: p.published_at || p.created_at,
  image: p.image_url || DEFAULT_BRAND.hero_image_url,
  category: p.category || 'Actualités',
});

const postPath = (post) => `/blog/${post.slug || post.id}`;
const postKey = (post) => String(post.slug || post.id);

const mapMockPost = (p) => ({
  ...p,
  content: p.content || p.excerpt || '',
});

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function estimateReadingTime(text = '') {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function ShareButtons({ title, url }) {
  const [copied, setCopied] = useState(false);
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const shareClass =
    'text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:text-[#3a00e0] hover:border-[rgba(58,0,224,0.35)] transition-colors';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-slate-500 mr-1 flex items-center">
        <Share2 className="h-4 w-4 mr-1.5" aria-hidden="true" />
        Partager
      </span>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={shareClass}
      >
        Facebook
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={shareClass}
      >
        X
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={shareClass}
      >
        LinkedIn
      </a>
      <button type="button" onClick={handleCopy} className={`${shareClass} inline-flex items-center gap-1.5`}>
        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Link2 className="h-3 w-3" />}
        {copied ? 'Copié !' : 'Copier le lien'}
      </button>
    </div>
  );
}

export default function BlogPost() {
  const { postId } = useParams();
  const brandName = useBrandName();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (BACKEND_URL) {
          const [postRes, listRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/posts/${postId}`),
            fetch(`${BACKEND_URL}/api/posts`),
          ]);
          if (postRes.ok) {
            const loaded = mapApiPost(await postRes.json());
            setPost(loaded);
            if (listRes.ok) {
              const list = await listRes.json();
              if (Array.isArray(list)) {
                setRelatedPosts(
                  list
                    .map(mapApiPost)
                    .filter((p) => postKey(p) !== String(postId) && String(p.id) !== String(postId))
                    .slice(0, 3)
                );
              }
            }
            return;
          }
        }
      } catch {
        // fallback mock
      }
      const mock = mockBlogPosts.find((p) => String(p.id) === String(postId));
      setPost(mock ? mapMockPost(mock) : null);
      setRelatedPosts(mockBlogPosts.filter((p) => String(p.id) !== String(postId)).slice(0, 3));
    };
    load().finally(() => setLoading(false));
  }, [postId]);

  const readingTime = useMemo(
    () => (post ? estimateReadingTime(`${post.excerpt} ${post.content}`) : 0),
    [post]
  );

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  usePageMeta({
    title: post ? `${post.title} | ${brandName}` : `Article | ${brandName}`,
    description: post?.excerpt || `Article du blog ${brandName} sur la robotique éducative.`,
    image: post?.image || '/images/hero.jpg',
    path: `/blog/${postId}`,
    type: 'article',
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-below-site-header pb-20 flex flex-col items-center justify-center text-slate-600">
          <Loader2 className="h-10 w-10 text-[#3a00e0] animate-spin mb-4" />
          <p>Chargement de l&apos;article…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-below-site-header pb-20 px-4 text-center max-w-lg mx-auto">
          <IconBadge icon={FileQuestion} variant="purple" size="lg" className="mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article introuvable</h1>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Cet article n&apos;existe pas ou a été supprimé. Consultez nos autres publications sur le
            blog.
          </p>
          <Link to="/blog">
            <Button className={BTN_PRIMARY}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <article className="pt-below-site-header pb-12 sm:pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#3a00e0] hover:underline underline-offset-2 mb-8 text-sm transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au blog
          </Link>

          <Badge variant="outline" className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] mb-4">
            {post.category}
          </Badge>

          <h1 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-slate-900 mb-6 leading-tight tracking-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 mb-6">
            {formatDate(post.date) && (
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </div>
            )}
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center">
              <BookOpen className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
              <span>{readingTime} min de lecture</span>
            </div>
          </div>

          <div className="mb-8 pb-8 border-b border-slate-200">
            <ShareButtons title={post.title} url={shareUrl} />
          </div>

          <img
            src={post.image}
            alt={post.title}
            className="w-full rounded-xl mb-10 object-cover max-h-[440px] shadow-lg shadow-[0_16px_40px_-20px_rgba(58,0,224,0.25)]"
          />

          {post.excerpt && (
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 font-medium border-l-4 border-[#3a00e0] pl-5">
              {post.excerpt}
            </p>
          )}

          <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-[1.8] [&_p]:mb-5 whitespace-pre-wrap">
            {post.content}
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Tag className="h-4 w-4" aria-hidden="true" />
              <span>{post.category}</span>
            </div>
            <Link to="/blog">
              <Button variant="outline" className={BTN_OUTLINE}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour au blog
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className="py-14 sm:py-16 px-4 bg-slate-50 border-t border-slate-200">
          <div className="max-w-5xl mx-auto">
            <SectionHeader
              eyebrow="À lire aussi"
              title={<>Articles <span className="text-gradient-brand">similaires</span></>}
              align="left"
              mb="mb-8"
              reveal
            />
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((related, i) => (
                <Reveal key={related.id} animation="fade-up" delay={i * 60}>
                  <article className="group h-full">
                    <Link to={postPath(related)} className="block h-36 overflow-hidden rounded-xl mb-3">
                      <img
                        src={related.image}
                        alt={related.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                    <Badge
                      variant="outline"
                      className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] w-fit mb-2 text-xs"
                    >
                      {related.category}
                    </Badge>
                    <h3 className="text-slate-900 text-base font-semibold line-clamp-2 mb-2 tracking-tight">
                      <Link to={postPath(related)} className="hover:text-[#3a00e0] transition-colors">
                        {related.title}
                      </Link>
                    </h3>
                    <p className="text-slate-500 text-sm line-clamp-2">{related.excerpt}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
